'use client';

import { useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import type { RelatedProduct } from '@/types/pdp';
import { RelatedCard } from '@/components/catalog/RelatedCard';
import { useT } from '@/hooks/useT';
import { usePriceConvert } from '@/lib/PriceContext';
import type { I18nKey } from '@/lib/i18n';

interface Props {
  title?: string;
  titleKey?: I18nKey;
  count?: number;
  items: RelatedProduct[];
  lang: string;
  moreLikeId?: string;
  addonsId?: string;
  totalCount?: number;
}

const ARROW: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
  border: '1px solid var(--v4-border)', background: 'var(--v4-panel-2)',
  color: 'var(--v4-fg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', transition: 'all .18s',
};

export function RelatedCarousel({ title, titleKey, count, items, lang: langProp, moreLikeId, addonsId, totalCount }: Props) {
  const t = useT();
  const { lang } = usePriceConvert();
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapRef   = useRef<HTMLDivElement>(null);

  const displayTitle = titleKey
    ? `${t(titleKey)}${count != null ? ` (${count})` : ''}`
    : (title ?? '');

  if (!items.length) return null;

  const isAddons   = !!addonsId;
  const seeAllHref = addonsId
    ? `/browse/addons/${addonsId}`
    : moreLikeId
    ? `/browse/morelike/${moreLikeId}`
    : '/catalog';
  const seeAllColor = 'var(--gs-green)';
  const seeAllBorder = 'color-mix(in srgb, var(--gs-green) 30%, transparent)';
  const endCardBorder = 'color-mix(in srgb, var(--gs-green) 25%, transparent)';
  const endCardBg     = 'color-mix(in srgb, var(--gs-green) 5%, transparent)';
  const endCardCircleBg     = 'color-mix(in srgb, var(--gs-green) 15%, transparent)';
  const endCardCircleBorder = 'color-mix(in srgb, var(--gs-green) 30%, transparent)';
  const endCardLabel  = isAddons ? 'add-ons' : 'games';

  const hasMore    = totalCount != null && totalCount > items.length;
  const showSeeAll = addonsId || moreLikeId;

  const scroll = (dir: -1 | 1) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (!wrapRef.current) return;
    if (document.activeElement && !wrapRef.current.contains(document.activeElement)) return;
    if (e.key === 'ArrowLeft')  { e.preventDefault(); scroll(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); scroll(1); }
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener('keydown', onKeyDown as any);
    return () => el.removeEventListener('keydown', onKeyDown as any);
  }, [onKeyDown]);

  return (
    <div ref={wrapRef} tabIndex={-1} style={{ outline: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 className="pdp-section-title" style={{ fontSize: 15, fontWeight: 800, color: 'var(--v4-fg)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 3, height: 16, background: 'var(--gs-green)', borderRadius: 2, display: 'block', flexShrink: 0 }} />
          {displayTitle}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => scroll(-1)} className="carousel-arrow" style={ARROW} aria-label={t('hero_prev')}><ChevronLeft size={15} /></button>
          <button onClick={() => scroll(1)}  className="carousel-arrow" style={ARROW} aria-label={t('hero_next')}><ChevronRight size={15} /></button>
          {showSeeAll && (
            <Link href={seeAllHref} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 700, color: seeAllColor, textDecoration: 'none',
              border: `1px solid ${seeAllBorder}`, borderRadius: 8, padding: '5px 12px',
            }}>
              {isAddons ? t('footer_all_addons') : t('footer_all_games')}
            </Link>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="related-scroll" style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '4px 0 16px', scrollSnapType: 'x mandatory', scrollbarWidth: 'none' }}>
        {items.map(item => <RelatedCard key={item.related_product_id} item={item} lang={lang ?? langProp} />)}

        {/* "See all" end-card — only when there are more items than shown */}
        {hasMore && showSeeAll && (
          <Link
            href={seeAllHref}
            style={{
              flexShrink: 0, width: 175, scrollSnapAlign: 'start',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 12, borderRadius: 12, textDecoration: 'none',
              border: `1px solid ${endCardBorder}`, background: endCardBg,
              padding: '24px 16px', cursor: 'pointer',
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: endCardCircleBg, border: `1px solid ${endCardCircleBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ArrowRight size={20} color={seeAllColor} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: seeAllColor, letterSpacing: '.02em' }}>
                {t('common_see_all')} {totalCount}
              </div>
              <div style={{ fontSize: 10, color: 'var(--v4-fg-3)', marginTop: 4 }}>
                {endCardLabel}
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
